import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { Suspense } from "react";
import { useSuspenseQuery, queryOptions } from "@tanstack/react-query";
import { SiteShell, PageHero } from "@/components/site/SiteShell";
import { PageOutro } from "@/components/site/PageEssentials";
import { Section, CTABand, FAQ } from "@/components/site/Blocks";
import { getStateHero } from "@/data/state-heroes";
import { ddaFaqs } from "@/lib/hud-data-faqs";
import { getStateDda } from "@/lib/hud-datasets.functions";
import { getState } from "@/data/states";
import { buildHead } from "@/lib/seo-head";
import { GovernmentAffiliationNotice } from "@/components/site/GovernmentAffiliationNotice";
import { LocalMarketNews } from "@/components/site/LocalMarketNews";
import { getMarketStats } from "@/lib/market-stats.functions";

const ddaOptions = (state: string) =>
  queryOptions({
    queryKey: ["stateDda", state],
    queryFn: () => getStateDda({ data: { state } }),
    staleTime: 24 * 60 * 60 * 1000,
  });

export const Route = createFileRoute("/difficult-development-areas/$state")({
  loader: async ({ params, context }) => {
    const state = getState(params.state);
    if (!state) throw notFound();
    context.queryClient.ensureQueryData(ddaOptions(state.abbrev));
    const marketStats = await getMarketStats({ data: { stateSlug: state.slug } }).catch(() => null);
    return { state, marketStats };
  },
  head: ({ params, loaderData }) => {
    const state = loaderData?.state;
    if (!state) return { meta: [{ title: "Difficult Development Areas by State | Simply Approved Mortgages" }] };
    const url = `/difficult-development-areas/${params.state}`;
    const title = `DDA in ${state.name} | Simply Approved`;
    const description = `HUD Difficult Development Areas in ${state.name} — metro SDDA ZIP codes and non-metro county DDAs. Qualifies for LIHTC's 30% basis boost.`;
    return buildHead({
      title,
      description,
      url,
      type: "article",
      keywords: `${state.name} difficult development areas, ${state.name} DDA lookup, ${state.name} SDDA ZIP codes, ${state.name} LIHTC basis boost, ${state.name} non-metro DDA counties, HUD DDA ${state.name} 2026, ${state.name} affordable housing high cost area, ${state.name} 130% qualified basis, DDA vs QCT ${state.name}, ${state.name} LIHTC investing`,
      breadcrumbs: [
        { name: "Home", url: "/" },
        { name: "Difficult Development Areas", url: "/difficult-development-areas" },
        { name: state.name, url },
      ],
      service: { name: "Difficult Development Area Lookup", state: state.name },
      faqs: ddaFaqs(state.name),
      image: getStateHero(state.slug)?.src,
      imageAlt: getStateHero(state.slug)?.alt ?? `HUD Difficult Development Areas in ${state.name}`,
      extraJsonLd: [
        {
          "@context": "https://schema.org",
          "@type": "Dataset",
          name: `2026 Difficult Development Areas — ${state.name}`,
          description,
          creator: { "@type": "GovernmentOrganization", name: "U.S. Department of Housing and Urban Development" },
          license: "https://www.huduser.gov/portal/datasets/qct.html",
          spatialCoverage: { "@type": "AdministrativeArea", name: state.name },
          temporalCoverage: "2026",
        },
      ],
    });
  },
  component: DdaStatePage,
  notFoundComponent: () => (
    <SiteShell>
      <Section>
        <p>State not found. <Link to="/difficult-development-areas" className="text-brand underline">See all states</Link>.</p>
      </Section>
    </SiteShell>
  ),
  errorComponent: ({ error }) => (
    <SiteShell>
      <Section>
        <p className="text-destructive">Couldn't load DDA data: {(error as Error).message}</p>
      </Section>
    </SiteShell>
  ),
});

function DdaStatePage() {
  const { state, marketStats } = Route.useLoaderData();

  return (
    <SiteShell>
      <PageHero
        eyebrow="HUD data"
        title={`Difficult Development Areas in ${state.name} (2026)`}
        subtitle={`HUD's ${state.name} DDA designations — metro SDDA ZIP codes and non-metro counties where costs run high against local incomes. Each qualifies for LIHTC's 30% basis boost.`}
        primaryLabel="Start My Approval"
        primaryHref="/apply"
        secondaryLabel="See Today's Rates"
        secondaryHref="/rates"
      />
      {/* Hero sits outside Suspense so the <h1> ships in the SSR HTML while the
          live HUD tables stream in. */}
      <Suspense fallback={<TableSkeleton />}>
        <DdaData state={state} />
      </Suspense>
      <Section>
        <div className="mx-auto max-w-3xl">
          <h2 className="mb-4 text-center font-display text-2xl font-bold text-foreground">
            {state.name} Difficult Development Areas — FAQs
          </h2>
          <FAQ items={ddaFaqs(state.name)} />
        </div>
      </Section>
      <LocalMarketNews
        stateName={state.name}
        stateAbbrev={state.abbrev}
        stats={marketStats ?? null}
        statsScope="state"
      />
      <div className="container mx-auto max-w-7xl px-4 pb-10">
        <GovernmentAffiliationNotice />
      </div>

      <CTABand
        title={`DDA project in ${state.name}?`}
        body="Send the address and we'll pull the FMR, confirm DDA/QCT overlap, and reply with DSCR pricing feedback."
      />
      <PageOutro />
    </SiteShell>
  );
}

function TableSkeleton() {
  return (
    <Section>
      <div className="mx-auto max-w-5xl animate-pulse space-y-4">
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-20 rounded-xl border border-border bg-card" />
          ))}
        </div>
        <div className="h-64 rounded-xl border border-border bg-card" />
      </div>
    </Section>
  );
}

function DdaData({ state }: { state: { name: string; abbrev: string; slug: string } }) {
  const { data: dda } = useSuspenseQuery(ddaOptions(state.abbrev));

  return (
    <Section>
      <div className="mx-auto max-w-5xl">
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
          <Stat label="Total DDAs" value={dda.total.toLocaleString()} />
          <Stat label="Metro SDDA ZIPs" value={dda.metro.length.toLocaleString()} />
          <Stat label="Non-Metro county DDAs" value={dda.nonMetro.length.toLocaleString()} />
        </div>

        {dda.nonMetro.length > 0 && (
          <>
            <h2 className="mt-12 font-display text-2xl font-bold text-foreground">Non-metro county DDAs</h2>
            <div className="mt-4 overflow-x-auto rounded-lg border border-border">
              <table className="w-full min-w-[420px] text-sm">
                <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="px-4 py-2">County</th>
                    <th className="px-4 py-2 font-mono">DDA code</th>
                  </tr>
                </thead>
                <tbody>
                  {dda.nonMetro.map((r) => (
                    <tr key={r.code} className="border-t border-border">
                      <td className="px-4 py-2 font-medium text-foreground">{r.name}</td>
                      <td className="px-4 py-2 font-mono text-xs text-muted-foreground">{r.code}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {dda.metro.length > 0 && (
          <>
            <h2 className="mt-12 font-display text-2xl font-bold text-foreground">Metro SDDA ZIP codes</h2>
            <div className="mt-4 overflow-x-auto rounded-lg border border-border">
              <table className="w-full min-w-[520px] text-sm">
                <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="px-4 py-2">ZIP</th>
                    <th className="px-4 py-2">Metro area</th>
                  </tr>
                </thead>
                <tbody>
                  {dda.metro.map((r) => (
                    <tr key={`${r.zcta}-${r.code}`} className="border-t border-border">
                      <td className="px-4 py-2 font-mono text-foreground">{r.zcta ?? "—"}</td>
                      <td className="px-4 py-2 text-muted-foreground">{r.name}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        <div className="mt-12 rounded-xl border border-border bg-card p-6">
          <h3 className="font-display text-xl font-semibold text-foreground">What is a Difficult Development Area?</h3>
          <p className="mt-2 text-sm text-muted-foreground">
            HUD designates a metro ZIP code (SDDA) or non-metro county as a DDA when its Fair Market Rent or construction
            costs are high relative to area median income. New LIHTC construction in a DDA qualifies for a <strong>130%
            qualified basis</strong> — the same 30% boost QCTs receive. For {state.name} investors, DDA and QCT overlays
            together map where affordable-housing capital is flowing hardest.
          </p>
        </div>

        <div className="mt-8 flex flex-wrap gap-3 text-sm">
          <Link to="/difficult-development-areas" className="rounded-md border border-border px-4 py-2 hover:border-brand">All states →</Link>
          <Link to="/qualified-census-tracts/$state" params={{ state: state.slug }} className="rounded-md border border-border px-4 py-2 hover:border-brand">
            QCTs in {state.name}
          </Link>
          <Link to="/hud-fair-market-rents/$state" params={{ state: state.slug }} className="rounded-md border border-border px-4 py-2 hover:border-brand">
            Fair Market Rents in {state.name}
          </Link>
          <Link to="/section-8-loans/$state" params={{ state: state.slug }} className="rounded-md bg-brand px-4 py-2 font-semibold text-primary-foreground hover:bg-brand/90">
            Section 8 DSCR loans in {state.name}
          </Link>
        </div>
      </div>
    </Section>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 shadow-sm">
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-1 font-display text-2xl font-bold text-foreground tabular-nums">{value}</div>
    </div>
  );
}
